import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Check } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useTenant } from '../../context/TenantContext';

const ClientBilling = () => {
  const { activeTenant } = useTenant();
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packages, setPackages] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loadingById, setLoadingById] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingError, setBillingError] = useState('');

  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoadingPackages(true);
        setLoadError('');
        const res = await api.get('/packages');
        setPackages(res.data || []);
      } catch (error) {
        console.error('Error al cargar paquetes:', error);
        setLoadError('No se pudieron cargar los planes. Intenta mas tarde.');
      } finally {
        setLoadingPackages(false);
      }
    };

    loadPackages();
  }, []);

  useEffect(() => {
    if (!activeTenant) return;
    const loadBilling = async () => {
      try {
        setLoadingBilling(true);
        setBillingError('');
        const [subRes, invRes] = await Promise.all([
          api.get('/billing/me', { params: { tenant_id: activeTenant } }),
          api.get('/billing/invoices', { params: { tenant_id: activeTenant } }),
        ]);
        setSubscription(subRes.data || null);
        setPaymentMethod(subRes.data?.payment_method || null);
        setInvoices(invRes.data || []);
      } catch (error) {
        console.error('Error cargando subscripcion:', error);
        setBillingError('No se pudo cargar la informacion de subscripcion.');
      } finally {
        setLoadingBilling(false);
      }
    };
    loadBilling();
  }, [activeTenant]);

  const visiblePackages = useMemo(
    () => (packages || []).filter((pkg) => pkg.id !== 'pkg-facturacion-basica'),
    [packages]
  );

  const featuredIndex = useMemo(() => {
    if (visiblePackages.length < 2) return -1;
    return Math.min(1, visiblePackages.length - 1);
  }, [visiblePackages.length]);

  const handleSubscribe = async (packageId) => {
    if (!activeTenant) {
      toast.error('Selecciona una empresa antes de suscribirte.');
      return;
    }
    setLoadingById((prev) => ({ ...prev, [packageId]: true }));
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/billing/checkout', {
        package_id: packageId,
        origin_url: originUrl,
        tenant_id: activeTenant,
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Error al crear checkout');
    } finally {
      setLoadingById((prev) => ({ ...prev, [packageId]: false }));
    }
  };

  const handleCancel = async () => {
    if (!activeTenant || !subscription?.subscription_status || subscription.subscription_status === 'canceled') {
      return;
    }
    const confirmed = window.confirm('Seguro que deseas cancelar la subscripcion?');
    if (!confirmed) return;
    try {
      await api.post('/billing/cancel', { tenant_id: activeTenant });
      toast.success('Subscripcion cancelada');
      const subRes = await api.get('/billing/me', { params: { tenant_id: activeTenant } });
      setSubscription(subRes.data || null);
      setPaymentMethod(subRes.data?.payment_method || null);
    } catch (error) {
      toast.error('Error al cancelar la subscripcion');
    }
  };

  const renderFeatures = (pkg) => {
    const features = [];
    if (pkg.description) {
      features.push(pkg.description);
    }
    if (pkg.included_tasks_summary) {
      features.push(pkg.included_tasks_summary);
    }
    if (pkg.sla_days !== undefined && pkg.sla_days !== null) {
      features.push(`SLA ${pkg.sla_days} dias`);
    }
    return features;
  };

  const currentPlan = useMemo(() => {
    if (!subscription?.plan_id) return null;
    return packages.find((pkg) => pkg.id === subscription.plan_id) || null;
  }, [packages, subscription]);

  const formatAmount = (amount, currency) => {
    if (amount === null || amount === undefined) return '-';
    const value = typeof amount === 'number' ? amount / 100 : amount;
    return `${value.toFixed(2)} ${currency?.toUpperCase() || ''}`.trim();
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    if (typeof ts === 'number') {
      return new Date(ts * 1000).toLocaleDateString();
    }
    return new Date(ts).toLocaleDateString();
  };

  return (
    <div data-testid="client-billing-page" className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-heading font-bold mb-2">Subscripcion</h1>
        <p className="text-muted-foreground">Gestiona tu plan, pago y facturas</p>
      </div>

      {!activeTenant && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          Selecciona una empresa para continuar con la suscripcion.
        </div>
      )}

      {loadingBilling && <p className="text-muted-foreground mb-6">Cargando subscripcion...</p>}
      {billingError && <p className="text-destructive mb-6">{billingError}</p>}

      <Card className="mb-8">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm text-muted-foreground">Plan actual</div>
              <div className="text-xl font-heading font-bold">
                {currentPlan?.name || subscription?.plan_id || 'Sin plan'}
              </div>
              <div className="text-sm text-muted-foreground">
                Estado: {subscription?.subscription_status || 'sin suscripcion'}
              </div>
              <div className="text-xs text-muted-foreground">
                Periodo: {subscription?.current_period_start ? formatDate(subscription.current_period_start) : '-'} a{' '}
                {subscription?.current_period_end ? formatDate(subscription.current_period_end) : '-'}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={!subscription?.subscription_status || subscription.subscription_status === 'canceled'}
            >
              Cancelar subscripcion
            </Button>
          </div>
          <div className="border-t pt-4">
            <div className="text-sm text-muted-foreground mb-2">Metodo de pago</div>
            {paymentMethod ? (
              <div className="text-sm">
                {paymentMethod.brand?.toUpperCase()} **** {paymentMethod.last4} · {paymentMethod.exp_month}/{paymentMethod.exp_year}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No hay metodo registrado.</div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-12">
        <CardContent className="p-6">
          <div className="text-lg font-semibold mb-4">Facturas pagadas</div>
          {invoices.length === 0 ? (
            <div className="text-sm text-muted-foreground">No hay facturas registradas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Importe</th>
                    <th className="px-4 py-2 text-left">Estado</th>
                    <th className="px-4 py-2 text-left">Descargar</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const downloadUrl = inv.invoice_pdf || inv.hosted_invoice_url || inv.receipt_url;
                    return (
                      <tr key={inv.id} className="border-t">
                        <td className="px-4 py-2">{formatDate(inv.created)}</td>
                        <td className="px-4 py-2">{formatAmount(inv.amount_paid, inv.currency)}</td>
                        <td className="px-4 py-2">{inv.status || '-'}</td>
                        <td className="px-4 py-2">
                          {downloadUrl ? (
                            <a className="underline" href={downloadUrl} target="_blank" rel="noreferrer">
                              Descargar
                            </a>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {loadingPackages && <p className="text-muted-foreground mb-6">Cargando planes...</p>}
      {loadError && <p className="text-destructive mb-6">{loadError}</p>}

      {!loadingPackages && !loadError && visiblePackages.length === 0 && (
        <p className="text-muted-foreground">No hay planes disponibles.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {visiblePackages.map((pkg, index) => {
          const features = renderFeatures(pkg);
          const isFeatured = index === featuredIndex;
          const isLoading = Boolean(loadingById[pkg.id]);
          return (
            <Card
              key={pkg.id}
              className="relative hover:border-primary hover:shadow-lg transition-all duration-300"
              data-testid={`package-${pkg.id}`}
            >
              {isFeatured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium shadow-md">
                    Mas Popular
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-heading mb-4">{pkg.name}</CardTitle>
                <div className="mb-4">
                  <span className="text-5xl font-heading font-bold">{pkg.price}</span>
                  <span className="text-muted-foreground ml-2">{pkg.currency}/mes</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(pkg.id)}
                  disabled={isLoading || !activeTenant}
                  className="w-full shadow-md hover:shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95 duration-200"
                  data-testid={`subscribe-${pkg.id}-button`}
                >
                  {isLoading ? 'Procesando...' : 'Suscribirse'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ClientBilling;
